import React, { useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, FileText, Ship, ArrowUp, ArrowDown, X, CheckCircle2, RotateCcw } from "lucide-react";
import { AppLayout, PageHeader } from "@/components/AppLayout";
import { DataTable, Toolbar, Btn, Field, Input, Select, Modal, ConfirmDialog, Textarea } from "@/components/Primitives";
import { api, saveBlob, formatApiErrorDetail, can } from "@/lib/api";
import { ContainerEstimate, calcCbmPerPole, stockBundleMap, withStockBundles } from "@/components/ContainerEstimate";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const empty = {
  shipment_number: "", vessel: "", voyage: "", booking_reference: "",
  export_destination: "", containers_booked: "", ship_date: "", notes: "",
  order_ids: [],
};

export default function Shipments() {
  const { user } = useAuth();
  const role = user?.role;
  const canCreate = can(role, "shipments:create");
  const [shipments, setShipments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [selected, setSelected] = useState(null);       // shipment detail
  const [detailOrders, setDetailOrders] = useState([]); // full order docs w/ line progress
  const [delItem, setDelItem] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [estMode, setEstMode] = useState("allocated"); // allocated | full
  const [includeUnalloc, setIncludeUnalloc] = useState(false); // planned rows on picking slip
  const planRef = useRef(null);

  const load = async () => {
    try {
      const [s, o] = await Promise.all([api.get("/shipments"), api.get("/orders")]);
      setShipments(s.data);
      setOrders(o.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => {
    load();
    api.get("/export-destinations").then((r) => setDestinations(r.data)).catch(() => {});
  }, []);

  const openNew = () => { setForm({ ...empty, order_ids: [] }); setEditingId(null); setOpen(true); };
  const openEdit = (s) => {
    setForm({
      shipment_number: s.shipment_number || "", vessel: s.vessel || "", voyage: s.voyage || "",
      booking_reference: s.booking_reference || "", export_destination: s.export_destination || "",
      containers_booked: s.containers_booked ?? "", ship_date: s.ship_date || "", notes: s.notes || "",
      order_ids: s.order_ids || [],
    });
    setEditingId(s.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.order_ids.length) { toast.error("Add at least one order to the shipment"); return; }
    try {
      const p = {
        ...form,
        containers_booked: form.containers_booked === "" ? null : Number(form.containers_booked),
        ship_date: form.ship_date || null,
      };
      if (editingId) {
        await api.put(`/shipments/${editingId}`, p);
        toast.success("Shipment updated");
      } else {
        const r = await api.post("/shipments", p);
        toast.success(`Shipment ${r.data.shipment_number} created`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/shipments/${delItem.id}`);
      toast.success("Shipment deleted");
      setDelItem(null);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const setStatus = async (s, status) => {
    try {
      await api.post(`/shipments/${s.id}/status`, { status });
      toast.success(status === "closed" ? `Shipment ${s.shipment_number} marked complete` : `Shipment ${s.shipment_number} reopened`);
      if (selected?.id === s.id) setSelected({ ...selected, status });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const openDetail = async (s) => {
    setSelected(s);
    setDetailOrders([]);
    try { setEstMode(localStorage.getItem(`shipmentEstimateMode:${s.id}`) || "allocated"); } catch { /* ignore */ }
    try { setIncludeUnalloc(localStorage.getItem(`shipmentIncludeUnalloc:${s.id}`) === "1"); } catch { /* ignore */ }
    try {
      const rs = await Promise.all((s.order_ids || []).map((id) => api.get(`/orders/${id}`)));
      setDetailOrders(rs.map((r) => r.data));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  const changeMode = (m) => {
    setEstMode(m);
    try { localStorage.setItem(`shipmentEstimateMode:${selected.id}`, m); } catch { /* ignore */ }
  };
  const changeIncludeUnalloc = (on) => {
    setIncludeUnalloc(on);
    try { localStorage.setItem(`shipmentIncludeUnalloc:${selected.id}`, on ? "1" : "0"); } catch { /* ignore */ }
    if (on && estMode !== "full") changeMode("full");
  };

  // Combined estimate input, tagged by order. Two bases:
  //  - allocated: only stock currently allocated to the orders (pickable now)
  //  - full: everything still to ship (allocated portion packs first, rest = "(plan)")
  const combinedLines = detailOrders.flatMap((o, idx) => {
    const sbMap = stockBundleMap(o.allocated_stock);
    return (o.line_items || []).flatMap((l) => {
      const alloc = Number(l.allocated_quantity) || 0;
      const remaining = Math.max(0, (Number(l.required_quantity) || 0) - (Number(l.dispatched_quantity) || 0));
      const base = { ...l, __tag: o.order_number, __pri: idx };
      if (estMode === "allocated") {
        return alloc > 0 ? [withStockBundles({ ...base, required_quantity: alloc, __alloc: true }, sbMap)] : [];
      }
      const planned = Math.max(0, remaining - alloc);
      const out = [];
      if (alloc > 0) out.push(withStockBundles({ ...base, required_quantity: alloc, __alloc: true }, sbMap));
      if (planned > 0) out.push({ ...base, required_quantity: planned, __alloc: false });
      return out;
    });
  });

  // spec lookup for picking-slip enrichment (colour / pointed / bundled per order line)
  const specMap = {};
  detailOrders.forEach((o) => (o.line_items || []).forEach((l) => {
    specMap[`${o.order_number}|${Number(l.length)}|${Number(l.butt_size)}`] = {
      colour: l.colour || null, pointed: !!l.requires_pointing, bundled: !!l.bundled,
    };
  }));

  // Build the container payload from the live estimate (shared by picking slips
  // and "Mark Ready to Dispatch"). allocOnly=true keeps only allocated rows.
  const buildContainers = (allocOnly = false) => {
    const plan = planRef.current;
    if (!plan || plan.stuck || !plan.containers?.length) {
      toast.error("No valid container plan — check the estimate rules");
      return null;
    }
    // Limit to the number of booked containers (estimator override first, then
    // the shipment's containers_booked). No booking → all containers.
    let booked = 0;
    let density = 900;
    try {
      const r = JSON.parse(localStorage.getItem(`containerEstimateRules:shipment:${selected.id}`) || "{}");
      booked = Math.floor(Number(r.booked)) > 0 ? Math.floor(Number(r.booked)) : 0;
      if (Number(r.density) > 0) density = Number(r.density);
    } catch { /* ignore */ }
    if (!booked) booked = Math.floor(Number(selected.containers_booked)) > 0 ? Math.floor(Number(selected.containers_booked)) : 0;
    const planned = booked > 0 ? plan.containers.slice(0, booked) : plan.containers;
    const keepUnalloc = includeUnalloc && !allocOnly;
    const all = planned.map((c) => ({
      cbm: c.cbm, wt: c.wt, floor: c.floor,
      ...(c.layoutManual ? {
        sections_spec: c.sections.map((sec) => ({
          length: sec.length,
          items: sec.contents.filter((x) => keepUnalloc || x.alloc !== false)
            .map((x) => ({ tag: x.tag, length: x.length, butt: x.butt, qty: x.qty, top: !!x.span && !x.under, under: !!x.under })),
        })),
      } : {}),
      rows: c.sections.flatMap((sec) => sec.contents
        .filter((x) => keepUnalloc || x.alloc !== false)
        .map((x) => ({
          tag: x.tag, qty: x.qty, length: x.length, butt: x.butt,
          planned: x.alloc === false,
          cbm: +(calcCbmPerPole(x.butt, x.length) * x.qty).toFixed(3),
          wt: Math.round(calcCbmPerPole(x.butt, x.length) * x.qty * density),
          ...(specMap[`${x.tag}|${x.length}|${x.butt}`] || {}),
        }))),
    }));
    return { containers: all.filter((c) => c.rows.length > 0), all, density, booked, planTotal: plan.containers.length };
  };

  const [splitAsk, setSplitAsk] = useState(null); // {fmt, payload, splits}

  const generateSlip = async (fmt, payload) => {
    setPdfBusy(true);
    try {
      const r = await api.post(`/shipments/${selected.id}/picking-slip.${fmt}`, payload, { responseType: "blob" });
      saveBlob(r, `picking-slip-${selected.shipment_number || selected.id}.${fmt}`);
    } catch (e) {
      toast.error("Failed to generate picking slip");
    } finally { setPdfBusy(false); }
  };

  const downloadPickingSlip = async (fmt = "pdf") => {
    setPdfBusy(true);
    try {
      let payload = {};
      if (!selected.dispatch_ready) {
        const built = buildContainers(false);
        if (!built) return;
        const { containers, all, booked, planTotal, density } = built;
        if (!containers.length) {
          toast.error(includeUnalloc
            ? "Nothing to print in this plan"
            : "No allocated stock in this plan — allocate stock to the orders first, or tick 'Include unallocated stock'");
          return;
        }
        if (containers.length < all.length) {
          toast.info(`Picking slip covers ${containers.length} of ${all.length} planned container(s) — only containers with ${includeUnalloc ? "stock" : "allocated stock"} are included`);
        } else if (booked > 0 && planTotal > booked) {
          toast.info(`Picking slip limited to the ${booked} booked container(s) — ${planTotal - booked} estimated container(s) beyond the booking were left off`);
        }
        payload = { containers, density };
      }
      // dry-run: does any bundle need breaking to match the plan exactly?
      const chk = await api.post(`/shipments/${selected.id}/picking-slip/check-splits`, payload);
      const splits = chk.data?.splits || [];
      if (splits.length) {
        setSplitAsk({ fmt, payload, splits });
        return;
      }
      const r = await api.post(`/shipments/${selected.id}/picking-slip.${fmt}`, payload, { responseType: "blob" });
      saveBlob(r, `picking-slip-${selected.shipment_number || selected.id}.${fmt}`);
    } catch (e) {
      toast.error("Failed to generate picking slip");
    } finally { setPdfBusy(false); }
  };

  const markReady = async () => {
    const built = buildContainers(true);
    if (!built) return;
    if (!built.containers.length) {
      toast.error("No allocated stock in the container plan — allocate stock to the orders first");
      return;
    }
    try {
      const r = await api.post(`/shipments/${selected.id}/mark-ready`, {
        containers: built.containers, density: built.density,
      });
      toast.success(`Ready to dispatch — ${r.data.containers} container(s) locked, ${r.data.assigned} stock line(s) assigned`);
      const upd = await api.get(`/shipments/${selected.id}`);
      setSelected(upd.data);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const unmarkReady = async () => {
    try {
      await api.post(`/shipments/${selected.id}/unmark-ready`);
      toast.success("Container plan unlocked — the estimate below is live again");
      const upd = await api.get(`/shipments/${selected.id}`);
      setSelected(upd.data);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const addOrder = (id) => {
    if (!id || form.order_ids.includes(id)) return;
    setForm((f) => ({ ...f, order_ids: [...f.order_ids, id] }));
  };
  const moveOrder = (i, dir) => setForm((f) => {
    const ids = [...f.order_ids];
    const j = i + dir;
    if (j < 0 || j >= ids.length) return f;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    return { ...f, order_ids: ids };
  });
  const orderLabel = (id) => {
    const o = orders.find((x) => x.id === id);
    return o ? `${o.order_number}${o.customer_name ? ` — ${o.customer_name}` : ""}` : id.slice(0, 8);
  };

  const columns = [
    { header: "Shipment #", mono: true, render: (r) => <span className="font-semibold">{r.shipment_number}</span> },
    { header: "Vessel", render: (r) => r.vessel || "—" },
    { header: "Booking Ref", mono: true, render: (r) => r.booking_reference || "—" },
    { header: "Destination", render: (r) => r.export_destination || "—" },
    { header: "Ship Date", mono: true, render: (r) => r.ship_date || "—" },
    { header: "Cont. Booked", align: "right", mono: true, render: (r) => r.containers_booked ?? "—" },
    { header: "Orders (priority)", render: (r) => (
      <span className="text-xs">{(r.orders || []).map((o) => o.order_number).join(" → ") || "—"}</span>
    )},
    { header: "Status", render: (r) => (
      <div className="flex flex-col gap-1 items-start">
        <span className={`text-xs uppercase tracking-wider px-2 py-0.5 rounded-full border ${r.status === "closed" ? "bg-stone-100 text-stone-600 border-stone-300" : "bg-emerald-50 text-emerald-800 border-emerald-200"}`}>
          {r.status === "closed" ? "complete" : r.status}
        </span>
        {r.dispatch_ready && (
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border bg-sky-50 text-sky-800 border-sky-200 font-semibold" data-testid={`shipment-ready-badge-${r.id}`}>
            dispatch ready
          </span>
        )}
      </div>
    )},
    { header: "Actions", align: "right", render: (r) => (
      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
        {canCreate && (
          <>
            <button onClick={() => openEdit(r)} className="p-1 text-stone-600 hover:text-sky-700" title="Edit" data-testid={`shipment-edit-${r.id}`}>
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => setDelItem(r)} className="p-1 text-stone-600 hover:text-red-700" title="Delete" data-testid={`shipment-delete-${r.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </>
        )}
      </div>
    )},
  ];

  return (
    <AppLayout>
      <PageHeader
        title="Shipments"
        subtitle="Vessel bookings that combine orders into container loads — estimate, plan and print picking slips."
        actions={canCreate && (
          <Btn onClick={openNew} testid="new-shipment-btn"><Plus className="h-4 w-4" /> New Shipment</Btn>
        )}
      />
      <Toolbar>
        <div className="text-xs text-stone-500">{shipments.length} shipment(s)</div>
      </Toolbar>
      <DataTable columns={columns} rows={shipments} onRowClick={openDetail}
                 empty="No shipments yet — create one to combine orders into container loads."
                 testid="shipments-table" />

      {open && (
        <Modal open onClose={() => setOpen(false)} title={editingId ? "Edit Shipment" : "New Shipment"} size="lg"
               testid="shipment-form-modal"
               footer={<><Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
                 <Btn onClick={save} testid="shipment-save">{editingId ? "Save Changes" : "Create Shipment"}</Btn></>}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {editingId && (
              <Field label="Shipment # (auto)">
                <Input value={form.shipment_number} readOnly disabled className="bg-stone-100 text-stone-500" data-testid="shipment-number" />
              </Field>
            )}
            <Field label="Vessel">
              <Input value={form.vessel} onChange={(e) => setForm({ ...form, vessel: e.target.value })} data-testid="shipment-vessel" />
            </Field>
            <Field label="Voyage">
              <Input value={form.voyage} onChange={(e) => setForm({ ...form, voyage: e.target.value })} data-testid="shipment-voyage" />
            </Field>
            <Field label="Booking Reference">
              <Input value={form.booking_reference} onChange={(e) => setForm({ ...form, booking_reference: e.target.value })} data-testid="shipment-booking-ref" />
            </Field>
            <Field label="Export Destination">
              <Select value={form.export_destination} onChange={(e) => setForm({ ...form, export_destination: e.target.value })} data-testid="shipment-destination">
                <option value="">Select...</option>
                {form.export_destination && !destinations.some((d) => d.code === form.export_destination) && (
                  <option value={form.export_destination}>{form.export_destination}</option>
                )}
                {destinations.filter((d) => d.active !== false).map((d) => <option key={d.id} value={d.code}>{d.code}</option>)}
              </Select>
            </Field>
            <Field label="Containers Booked">
              <Input type="number" min="0" value={form.containers_booked}
                     onChange={(e) => setForm({ ...form, containers_booked: e.target.value })} data-testid="shipment-containers-booked" />
            </Field>
            <Field label="Ship Date">
              <Input type="date" value={form.ship_date} onChange={(e) => setForm({ ...form, ship_date: e.target.value })} data-testid="shipment-ship-date" />
            </Field>
            <div className="md:col-span-2">
              <Field label="Notes">
                <Textarea rows={1} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </Field>
            </div>
          </div>

          <div className="mt-3">
            <div className="data-label mb-1.5">Orders on this shipment — picking priority top → bottom</div>
            <div className="space-y-1 mb-2" data-testid="shipment-order-list">
              {form.order_ids.map((id, i) => (
                <div key={id} className="flex items-center gap-2 text-sm bg-stone-50 border border-stone-200 rounded px-2 py-1.5">
                  <span className="font-mono font-bold text-sky-900 w-6">#{i + 1}</span>
                  <span className="flex-1">{orderLabel(id)}</span>
                  <button onClick={() => moveOrder(i, -1)} disabled={i === 0} className="p-1 text-stone-500 hover:text-sky-700 disabled:opacity-30" data-testid={`shipment-order-up-${i}`}><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button onClick={() => moveOrder(i, 1)} disabled={i === form.order_ids.length - 1} className="p-1 text-stone-500 hover:text-sky-700 disabled:opacity-30" data-testid={`shipment-order-down-${i}`}><ArrowDown className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setForm((f) => ({ ...f, order_ids: f.order_ids.filter((x) => x !== id) }))} className="p-1 text-stone-500 hover:text-red-700" data-testid={`shipment-order-remove-${i}`}><X className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {form.order_ids.length === 0 && <div className="text-xs text-stone-400">No orders added yet.</div>}
            </div>
            <Select value="" onChange={(e) => addOrder(e.target.value)} data-testid="shipment-add-order">
              <option value="">+ Add order…</option>
              {orders
                .filter((o) => !form.order_ids.includes(o.id) && !["dispatched", "invoiced", "closed", "cancelled"].includes(o.status))
                .map((o) => <option key={o.id} value={o.id}>{o.order_number}{o.customer_name ? ` — ${o.customer_name}` : ""}</option>)}
            </Select>
          </div>
        </Modal>
      )}

      {selected && (
        <Modal open onClose={() => { setSelected(null); setDetailOrders([]); }} size="xl"
               title={`Shipment ${selected.shipment_number || ""}`} testid="shipment-detail-modal"
               footer={<><Btn variant="secondary" onClick={() => { setSelected(null); setDetailOrders([]); }}>Close</Btn>
                 {canCreate && (selected.status === "closed" ? (
                   <Btn variant="secondary" onClick={() => setStatus(selected, "open")} testid="shipment-reopen-btn">
                     <RotateCcw className="h-4 w-4" /> Reopen
                   </Btn>
                 ) : (
                   <Btn variant="secondary" onClick={() => setStatus(selected, "closed")} testid="shipment-complete-btn">
                     <CheckCircle2 className="h-4 w-4" /> Mark Complete
                   </Btn>
                 ))}
                 <Btn onClick={() => downloadPickingSlip("pdf")} disabled={pdfBusy || !detailOrders.length} testid="shipment-picking-slip-btn">
                   <FileText className="h-4 w-4" /> {pdfBusy ? "Generating…" : "Picking Slip (PDF)"}
                 </Btn>
                 <Btn variant="secondary" onClick={() => downloadPickingSlip("xlsx")} disabled={pdfBusy || !detailOrders.length} testid="shipment-picking-slip-xlsx-btn">
                   <FileText className="h-4 w-4" /> Picking Slip (Excel)
                 </Btn></>}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4 pb-3 border-b border-stone-200">
            <div><div className="data-label">Vessel</div><div>{selected.vessel || "—"}{selected.voyage ? ` · ${selected.voyage}` : ""}</div></div>
            <div><div className="data-label">Booking Ref</div><div className="font-mono">{selected.booking_reference || "—"}</div></div>
            <div><div className="data-label">Destination</div><div>{selected.export_destination || "—"}</div></div>
            <div><div className="data-label">Ship Date</div><div className="font-mono">{selected.ship_date || "—"}</div></div>
          </div>
          <div className="mb-3">
            <div className="data-label mb-1.5">Orders — picking priority</div>
            <div className="flex flex-wrap gap-2">
              {detailOrders.map((o, i) => {
                const remaining = (o.line_items || []).reduce((a, l) => a + Math.max(0, (Number(l.required_quantity) || 0) - (Number(l.dispatched_quantity) || 0)), 0);
                const alloc = (o.line_items || []).reduce((a, l) => a + (Number(l.allocated_quantity) || 0), 0);
                return (
                  <div key={o.id} className="text-xs bg-stone-50 border border-stone-200 rounded px-2 py-1.5" data-testid={`shipment-detail-order-${i}`}>
                    <span className="font-mono font-bold text-sky-900">#{i + 1}</span>{" "}
                    <span className="font-semibold">{o.order_number}</span>
                    {o.customer_name ? ` · ${o.customer_name}` : ""} · <span className="font-mono text-emerald-800">{alloc} allocated</span> · <span className="font-mono">{remaining}</span> to ship
                  </div>
                );
              })}
              {!detailOrders.length && <div className="text-xs text-stone-400">Loading orders…</div>}
            </div>
          </div>
          {selected.dispatch_ready ? (
            <div className="flex flex-wrap items-center gap-2 mb-3 px-3 py-2 rounded border border-sky-300 bg-sky-50 text-sm" data-testid="shipment-ready-banner">
              <CheckCircle2 className="h-4 w-4 text-sky-700 shrink-0" />
              <span className="font-semibold text-sky-900 uppercase tracking-wider text-xs">Ready to Dispatch</span>
              <span className="text-xs text-sky-800">
                {selected.dispatch_plan?.containers?.length || 0} container(s) locked
                {selected.dispatch_plan?.marked_at ? ` on ${selected.dispatch_plan.marked_at.slice(0, 10)}` : ""} —
                the Dispatch page groups stock by these containers and picking slips print this exact plan.
              </span>
              {canCreate && (
                <button onClick={unmarkReady}
                        className="ml-auto inline-flex items-center gap-1 rounded-sm border border-sky-300 bg-white px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-sky-800 hover:bg-sky-100"
                        data-testid="shipment-unmark-ready">
                  <RotateCcw className="h-3 w-3" /> Unmark / unlock
                </button>
              )}
            </div>
          ) : null}
          {detailOrders.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-2" data-testid="shipment-est-mode">
              <span className="data-label">Estimate basis:</span>
              <button onClick={() => changeMode("allocated")}
                      className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${estMode === "allocated" ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-stone-600 border-stone-300 hover:border-emerald-700"}`}
                      data-testid="shipment-est-mode-allocated">
                Allocated stock only
              </button>
              <button onClick={() => changeMode("full")}
                      className={`text-xs px-2.5 py-1 rounded-full border font-semibold ${estMode === "full" ? "bg-emerald-800 text-white border-emerald-800" : "bg-white text-stone-600 border-stone-300 hover:border-emerald-700"}`}
                      data-testid="shipment-est-mode-full">
                Full order (remaining)
              </button>
              {estMode === "full" && (
                <span className="text-[11px] text-stone-500">unallocated quantities show as “(plan)”</span>
              )}
              <label className="flex items-center gap-1.5 text-xs text-stone-700 cursor-pointer ml-2" data-testid="shipment-include-unalloc">
                <input type="checkbox" checked={includeUnalloc} onChange={(e) => changeIncludeUnalloc(e.target.checked)} />
                Include unallocated stock on the picking slip <span className="text-stone-400">(marked “NOT YET ALLOCATED”)</span>
              </label>
              {canCreate && !selected.dispatch_ready && (
                <Btn size="sm" onClick={markReady} testid="shipment-mark-ready">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Mark Ready to Dispatch
                </Btn>
              )}
            </div>
          )}
          {detailOrders.length > 0 && (
            combinedLines.length > 0 ? (
              <ContainerEstimate
                key={`${selected.id}:${estMode}`}
                lines={combinedLines}
                storageKey={`containerEstimateRules:shipment:${selected.id}`}
                defaultBooked={selected.containers_booked || ""}
                onPlan={(res) => { planRef.current = res; }}
                testid="shipment-container-estimate"
              />
            ) : (
              <div className="text-sm text-stone-500" data-testid="shipment-nothing-to-ship">
                {estMode === "allocated"
                  ? "No stock is allocated to these orders yet — allocate stock on the Orders page, or switch to Full order."
                  : "Nothing left to ship — all order lines on this shipment are fully dispatched."}
              </div>
            )
          )}
          <div className="text-[11px] text-stone-500 mt-2 flex items-center gap-1.5">
            <Ship className="h-3.5 w-3.5" />
            {estMode === "allocated"
              ? "The estimate packs only stock currently ALLOCATED to these orders, order #1 first. The picking slip covers exactly these containers."
              : "The estimate packs everything still to ship (allocated portions first, unallocated as “(plan)”). The picking slip only includes containers with allocated stock."}
          </div>
        </Modal>
      )}

      {splitAsk && (
        <Modal open onClose={() => { setSplitAsk(null); setPdfBusy(false); }} title="Bundle split required" size="md">
          <div className="space-y-3" data-testid="split-ask-modal">
            <p className="text-sm text-stone-600">
              The planned container quantities don't line up with whole bundles. The following
              bundle(s) would need to be broken open to match the plan exactly:
            </p>
            <ul className="text-sm font-mono space-y-1 bg-stone-50 border border-stone-200 rounded-sm p-3">
              {splitAsk.splits.map((sp, i) => (
                <li key={i} data-testid={`split-line-${i}`}>
                  Container {sp.container} — {sp.tag} · {sp.length}m × {sp.butt}mm: needs{" "}
                  <b>{sp.need}</b> of bundle <b>{sp.bundle_number}</b> ({sp.bundle_size} poles)
                </li>
              ))}
            </ul>
            <p className="text-sm text-stone-600">
              Can these bundle(s) be split? If not, quantities are rounded to whole bundles and
              the difference is carried to the next container.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <Btn variant="secondary" testid="keep-bundles-whole-btn"
                   onClick={() => { const a = splitAsk; setSplitAsk(null); generateSlip(a.fmt, { ...a.payload, allow_split: false }); }}>
                No — keep bundles whole
              </Btn>
              <Btn testid="split-bundles-btn"
                   onClick={() => { const a = splitAsk; setSplitAsk(null); generateSlip(a.fmt, { ...a.payload, allow_split: true }); }}>
                Yes — split the bundle(s)
              </Btn>
            </div>
          </div>
        </Modal>
      )}

      {delItem && (
        <ConfirmDialog
          open
          title="Delete shipment?"
          message={`Delete shipment ${delItem.shipment_number}? Orders are not affected.`}
          onClose={() => setDelItem(null)}
          onConfirm={doDelete}
          testid="shipment-delete-confirm"
        />
      )}
    </AppLayout>
  );
}
